<template>
  <!--Evento disparado ao selecionar um item 
      @event change
      @property {any} value - Valor selecionado no select
  -->
  <v-select
    outlined
    class="zoox-component zoox-select"
    append-icon="$keyboard_arrow_down_rounded"
    persistent-hint
    :style="inputRadius"
    :readonly="readonly"
    :class="classes"
    :error-messages="errorMessages"
    :hint="hint"
    :small-chips="chips"
    :chips="chips"
    :multiple="multiple"
    :deletable-chips="deletableChips"
    :label="currentLabel"
    :rules="rules"
    :disabled="disabled"
    :dense="isDense"
    :value="value"
    :items="items"
    :item-text="itemText"
    :item-value="itemValue"
    :hide-details="hideDetails"
    :clearable="clearable"
    :menu-props="menuProps"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <template v-if="multiple && !$scopedSlots.item" #item="{attrs, item}">
      <v-list-item-action>
        <z-icon>
          {{
            attrs.inputValue
              ? '$check_box_rounded'
              : '$check_box_outline_blank_rounded'
          }}
        </z-icon>
      </v-list-item-action>

      <v-list-item-content>
        <v-list-item-title>
          {{ item[itemText] || item }}
        </v-list-item-title>
      </v-list-item-content>
    </template>

    <template v-else-if="$scopedSlots.item" #item="props">
      <!--@slot Slot para customização do item do select-->
      <slot name="item" v-bind="props"> </slot>
    </template>

    <template v-if="shouldRenderSelectionSlot" #selection="{item, ...props}">
      <!-- @slot Define uma aparencia customizada para o selection 
            @binding {object} item Objeto referente ao item passado
      -->
      <slot name="selection" :item="item" v-bind="props">
        <z-chip
          v-if="chips"
          class="my-1"
          :x-small="!large"
          :color="color || 'primary'"
          selected
          :text="item.text"
        />
      </slot>

      <template
        v-if="truncateValues && !chips && multiple && props.index === 0"
      >
        <div class="truncate">
          {{ getAllSelectedValues }}
        </div>
      </template>
    </template>

    <template v-if="toggleAll && multiple" #prepend-item>
      <div v-ripple tabindex="0" class="zoox-select-all" @click="onToggleAll">
        <div class="action">
          <z-icon :color="value && value.length > 0 ? 'primary' : ''">{{
            value && value.length > 0
              ? '$check_box_rounded'
              : '$check_box_outline_blank_rounded'
          }}</z-icon>
        </div>

        <div :style="{fontSize: isDense ? '.8125rem' : '1rem'}" class="content">
          {{
            value && value.length > 0 ? 'Desmarcar Todos' : 'Selecionar Todos'
          }}
        </div>
      </div>
    </template>
  </v-select>
</template>

<script>
import inputControlPropsMixin from '../inputControlPropsMixin'

/**
 * @displayName Select
 */
export default {
  name: 'ZSelect',
  mixins: [inputControlPropsMixin],
  model: {
    prop: 'value',
    event: 'change',
  },

  props: {
    /**
     * Select items
     *
     * @typedef Object {text:string,value:any}
     */
    items: {
      type: Array,
      required: true,
    },
    /**
     * Adiciona funcionalidade de "limpar" o input
     */
    clearable: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Customiza a chave 'text' no array de itens
     */
    itemText: {
      type: String,
      required: false,
      default: 'text',
    },
    /**
     * Customiza a chave 'value' no array de itens
     */
    itemValue: {
      type: String,
      required: false,
      default: 'value',
    },
    /**
     * Quando true, aplica o estilo de chip no item selecionado
     */
    chips: {
      type: Boolean,
      required: false,
    },
    /**
     * Aplica um ícone para excluir o chip selecionado
     */
    deletableChips: {
      type: Boolean,
      required: false,
    },
    /**
     * Permite a seleção de multiplos itens. `value` deve ser um array
     */
    multiple: {
      type: Boolean,
      required: false,
    },
    /**
     * Aplica cor ao componente
     */
    color: {
      type: String,
      required: false,
    },
    /**
     * Aplica funcionalidade de "selecionar todos" quando a prop `multiple` é usada
     */
    toggleAll: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Trunca os valores do select quando a prop `multiple` é true e a prop `chips` é falsa
     */
    truncateValues: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * Props repassadas para o componente `v-menu` usado internamente no z-select
     */
    menuProps: {
      type: Object,
      required: false,
      default: () => ({
        bottom: true,
        offsetY: true,
        offsetOverflow: true,
      }),
    },

    /**
     * Coloca o select em modo readonly
     */
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * Posição do label no select
     *
     * @values left, right, center
     */
    labelPosition: {
      type: String,
      required: false,
      default: 'left',
    },

    /**
     * Posição do valor no select
     */
    valuePosition: {
      type: String,
      required: false,
      default: 'left',
    },
    /**
     * Customiza o radius do input
     */
    radius: {
      type: Number,
      required: false,
      default: 0,
    },
  },

  computed: {
    classes() {
      return {
        small: this.small,
        'label-right': this.labelPosition === 'right',
        'label-center': this.labelPosition === 'center',
        'value-center': this.valuePosition === 'center',
        'value-right': this.valuePosition === 'right',
      }
    },

    getAllSelectedValues() {
      if (this.itemsAreStrings) return this.value.join(', ')

      return this.value.map((item) => item[this.itemText]).join(', ')
    },

    shouldRenderSelectionSlot() {
      return (
        this.$scopedSlots.selection ||
        this.chips ||
        (this.truncateValues && this.multiple && !this.chips)
      )
    },

    itemsAreStrings() {
      return this.items.every((item) => typeof item === 'string')
    },

    inputRadius() {
      if (this.radius) {
        return `border-radius: ${this.radius}px !important`
      }

      if (this.small) {
        return 'border-radius: var(--component-input-sm-radius) !important'
      }

      return 'border-radius: var(--component-input-md-radius) !important'
    },
  },

  methods: {
    onToggleAll() {
      this.$nextTick(() => {
        if (this.value && this.value.length > 0) {
          this.$emit('change', [])
        } else {
          this.$emit(
            'change',
            this.items.map((item) =>
              this.itemsAreStrings ? item : item[this.itemValue]
            )
          )
        }
      })
    },
  },
}
</script>

<style lang="scss">
@use '@scss/global' as g;

.zoox-select {
  &.small {
    @include g.zoox-small-form-input;
    .v-select__selections {
      font-size: var(--zoox-font-size-body3);
    }
  }
}
</style>

<style lang="scss" scoped>
.v-list {
  padding: 0 !important;
}

.truncate {
  width: 100%;
  height: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.zoox-select-all {
  display: flex;
  align-items: center;
  flex: 1 1 100%;
  padding: 0 16px;
  cursor: pointer;
  min-height: 40px;
  height: 40px;
  position: relative;
  user-select: none;
  border-bottom: 1px solid #cecece;

  & > :first-child {
    margin-right: 32px;
    display: inline-flex;
    min-width: 24px;
    align-self: center;
  }

  & > :last-child {
    flex: 1 1;
    padding: 12px 0;
    display: flex;
    align-self: center;
    align-items: center;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.zoox-select {
  border-radius: var(--component-input-md-radius) !important;

  :deep(.v-select__selections) {
    padding: 6px 0 0 2px !important;
  }

  :deep(.v-select.v-input--dense .v-select__selection--comma) {
    margin: 0 2px !important;
  }

  &:not(.error--text) {
    :deep(.v-input__control) {
      .v-input__slot:hover {
        fieldset {
          color: var(--primary) !important;
        }
      }
    }
  }

  &.value-center {
    :deep(.v-select__selections) {
      justify-content: center;

      & > input:not(:only-child) {
        display: none;
      }
    }
  }

  &.value-right {
    :deep(.v-select__selections) {
      justify-content: flex-end;

      & > input:not(:only-child) {
        display: none;
      }
    }
  }

  &.label-right {
    :deep(.v-label:not(.v-label--active)) {
      left: unset !important;
      right: calc(0px + 40px) !important;
    }
  }

  &.label-center {
    :deep(.v-label:not(.v-label--active)) {
      left: calc(50% - 40px) !important;
    }
  }

  &.small {
    :deep(.v-select__selection--comma) {
      margin-top: 0 !important;
    }
  }
}
</style>
